// A guarda de PLAUSIBILIDADE do resultado da simulação (spec-007, US-2b).
//
// ★ Ela mora na SAÍDA, não na entrada, e a razão é o defeito que a mediu:
// validar a entrada pega o campo AUSENTE, mas não pega o campo presente e
// inútil (`rent: null`, `auc: '5.333.578,72'`, um `alloc` que não soma 1). A
// saída pega os dois, porque é onde o defeito se manifesta — e o defeito, aqui,
// tem forma conhecida: `NaN`.
//
// Medido em 24/08/2026: com um `S` sem `rent`/`alfa`/`aporteAnual`, o
// `simulate()` devolve os custos em BPS CERTOS e todos os valores em REAIS como
// `NaN` (`patrOf` e `custosDaSerie`, `fee-lab-engine.js:581-615`). Nada estoura.
// E `JSON.stringify(NaN)` é `null`, então o payload que atravessa a rede parece
// um cenário completo com alguns campos "vazios".
//
// Publicar isso é pior que não publicar: os bps sustentam a leitura de que o
// cenário saiu inteiro, e o que falta é exatamente o que o consultor mostra ao
// cliente — economia e ganho de patrimônio. É a mesma regra do
// `premissasDoEscritorio()`, que LANÇA em vez de cair no `DEF`: erro visível é
// melhor que número errado calado.

/** Um número que dá para publicar: existe, é finito e não é `NaN`. */
const numeroPublicavel = (valor) =>
  typeof valor === 'number' && Number.isFinite(valor)

/**
 * As grandezas em REAIS que o cartão promete — e que são justamente as que
 * somem quando o `S` vem incompleto.
 *
 * Os BPS ficam de fora de propósito: eles saem certos mesmo com o `S`
 * incompleto, então incluí-los na guarda a deixaria verde no caso que ela
 * existe para pegar.
 */
const valoresEmReais = (resultado) => {
  const r = resultado || {}
  return [
    r.save && r.save.cliente,
    r.patrGain && r.patrGain.fee,
    r.patrimonio && r.patrimonio.fee,
    r.accComis,
    r.accFee,
  ]
}

/**
 * O resultado da simulação pode virar número na tela?
 *
 * @param {Object} resultado A saída de `simulate(S)`.
 * @returns {boolean} `false` quando qualquer grandeza em reais não é número.
 */
export const resultadoPublicavel = (resultado) =>
  Boolean(resultado) && valoresEmReais(resultado).every(numeroPublicavel)

/**
 * A evolução patrimonial pode ser publicada?
 *
 * Separada da anterior porque o modo de falha é OUTRO: sem o bloco de
 * longevidade o `simulateEvolucaoPatrimonio` não devolve `NaN` — devolve UM
 * ponto (`ages: [0]`, `delta: 0`). Um gráfico de um ponto é indistinguível de
 * "este cliente não evolui", que é uma afirmação sobre a vida do cliente que
 * ninguém apurou.
 *
 * @param {Object} evolucao A saída de `simulateEvolucaoPatrimonio(S)`.
 * @returns {boolean} `false` quando a série não tem trajetória.
 */
export const evolucaoPublicavel = (evolucao) => {
  const e = evolucao || {}
  return Array.isArray(e.ages) && e.ages.length > 1
}
